










import { UserDataModel, UserPoolDataModel, UserPoolData } from '@/common/userPoolData';
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import EventBus, { EVENTS, TOAST } from '@/eventBus';
import { allAvailableGroups, UserType } from '@qmu/common/enum/UserTypes';
import store, { GettersTypes, ActionsTypes } from '@/store';
import { MultiTenantGroupsInfo, TenantGroupsInfo } from '@/utils/Types/TenantGroupsInfoType';

interface UserGroupItemModel {
  name: string;
  guid: string;
  srNo: number;
  memberShip: boolean;
}

@Component
export default class GroupHandling extends Vue {
  @Prop({ default: null }) public userDataModel!: UserDataModel | null;
  @Prop({ default: false }) public applyClicked!: boolean;
  userPoolData: UserPoolDataModel = new UserPoolData();
  loading = false;
  tenantGroupList: string[] = [];
  memberOf: string[] = [];
  initialItems: Array<UserGroupItemModel> = [];
  updatableItems: Array<UserGroupItemModel> = [];
  tenantGroupInfo: TenantGroupsInfo = {};

  created() {
    this.fetchUserGroupInfo();
  }

  async getPromisesOfAssignOrRemove(removableItems: string[], assignableItems: string[], tenantId: string, userId: string): Promise<Array<boolean>> {
    const addRemoveGroup = [];
    if (removableItems.length)  addRemoveGroup.push(await this.userPoolData.removeUserFromGroup(removableItems, tenantId, userId))
    if (assignableItems.length) addRemoveGroup.push(await this.userPoolData.assignUserToGroup(assignableItems, tenantId, userId))
    return addRemoveGroup;
  }

  @Watch('applyClicked')
  async submitRequest() {
    if (!this.userDataModel?.username || !this.userDataModel?.userId || !this.userDataModel?.tenantId || !this.userDataModel?.groups) {
      EventBus.$emit(EVENTS.SHOW_TOAST, 'Something is not right. Please refresh your browser and try again', TOAST.ERROR);
      return;
    }
    this.loading = true;
    const removableItems = this.getRemovableGroups(this.initialItems, this.updatableItems);
    const assignableItems = this.getAssignableGroups(this.initialItems, this.updatableItems);

    const resp = await this.getPromisesOfAssignOrRemove(removableItems, assignableItems, this.userDataModel.tenantId, this.userDataModel.userId);

    const success = !resp.some(status => !status);

    if (success && (removableItems.length || assignableItems.length)) {
      this.userDataModel.groups.length = 0;
      this.userDataModel.groups.push(...this.getAllUpdatedGroups(this.updatableItems));
      EventBus.$emit(EVENTS.SHOW_TOAST, 'User group info updated successfully', TOAST.SUCCESS);
    } else if (!success) {
      EventBus.$emit(EVENTS.SHOW_TOAST, 'Something went wrong. Please retry', TOAST.ERROR);
    } else {
      EventBus.$emit(EVENTS.SHOW_TOAST, 'No changes applied', TOAST.INFO);
    }

    this.loading = false;
    this.$dialog.hideGroupHandlingDialog();
  }

  getHeaders() {
    return [
      { text: 'Serial #', value: 'srNo', align: 'center' },
      { text: 'Group Name', value: 'name', align: 'center' },
      { text: 'MemberShip', value: 'memberShip', align: 'center' },
    ];
  }

  async getItems(tenantList: string[]) {
    return tenantList.map((item, index) => {
      return {
        name: this.userReadAbleGroupName(item),
        guid: item,
        srNo: index + 1,
        memberShip: this.memberOf.includes(item),
      };
    });
  }

  userReadAbleGroupName(GUID: string) {
    return this.tenantGroupInfo[GUID].groupName;
  }

  async getMemberOf() {
    if (!this.userDataModel?.userId || !this.userDataModel?.tenantId) {
      EventBus.$emit(EVENTS.SHOW_TOAST, 'Something is not right. Please refresh your browser and try again', TOAST.ERROR);
      return [];
    }
    return this.userDataModel
      ? (await this.userPoolData.getGroupsOfUser(this.userDataModel?.tenantId, this.userDataModel?.userId)).filter(el => {
          return this.tenantGroupList?.includes(el);
        })
      : [];
  }

  getAllowedGroups(forGroup: UserType) {
    if (forGroup === UserType.TENANT_ADMIN || (store.getters[GettersTypes.GET_SELECTED_TENANT] as string) !== 'qbics') {
      const availableGroups = allAvailableGroups();
      if (availableGroups.includes(UserType.SUPER_ADMIN)) {
        availableGroups.splice(availableGroups.indexOf(UserType.SUPER_ADMIN), 1);
      }
      return availableGroups;
    }
    if (forGroup === UserType.SUPER_ADMIN) return allAvailableGroups();
    return [];
  }

  async fetchUserGroupInfo() {
    this.loading = true;
    if (!this.userDataModel?.tenantId) {
      EventBus.$emit(EVENTS.SHOW_TOAST, 'Something is not right. Please refresh your browser and try again', TOAST.ERROR);
      return [];
    }
    this.tenantGroupInfo = (store.getters[GettersTypes.GET_TENANT_GROUPS_INFO] as MultiTenantGroupsInfo)[this.userDataModel.tenantId];

    try {
      this.tenantGroupList = Object.keys(this.tenantGroupInfo);
      this.memberOf = await this.getMemberOf();
      this.updatableItems = await this.getItems(this.tenantGroupList);
      this.initialItems = cloneDeep(this.updatableItems); // clone deep to avoid reference issues
    } catch (error) {
      EventBus.$emit(EVENTS.SHOW_TOAST, 'User group info fetching failed', TOAST.ERROR);
      this.initialItems.length = 0;
      this.updatableItems.length = 0;
      this.memberOf.length = 0;
      this.tenantGroupList.length = 0;
    }
    this.loading = false;
  }

  getAllUpdatedGroups(updatableItems: Array<UserGroupItemModel>) {
    const updatedGroups: string[] = [];
    updatableItems.forEach(el => {
      if (el.memberShip) updatedGroups.push(el.name);
    });
    return updatedGroups;
  }

  getRemovableGroups(initialItems: Array<UserGroupItemModel>, updatableItems: Array<UserGroupItemModel>): Array<string> {
    const removableGroups: string[] = [];
    initialItems.forEach(el => {
      if (el.memberShip && updatableItems.find(item => item.guid === el.guid && !item.memberShip)) {
        removableGroups.push(el.guid);
      }
    });
    return removableGroups;
  }

  getAssignableGroups(initialItems: Array<UserGroupItemModel>, updatableItems: Array<UserGroupItemModel>): Array<string> {
    const assignableGroups: string[] = [];
    initialItems.forEach(el => {
      if (!el.memberShip && updatableItems.find(item => item.guid === el.guid && item.memberShip)) {
        assignableGroups.push(el.guid);
      }
    });
    return assignableGroups;
  }
}
